import React, { useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Check, CreditCard, Loader2, MessageCircle } from "lucide-react";
import { FORMATIONS } from "../mock";
import BookingDialog from "./BookingDialog";
import { createCheckout } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const LOOKUP_KEYS = { pack1: "formation_pack1", pack2: "formation_pack2" };

const Formations = () => {
  const { toast } = useToast();
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  const handleQuestion = (f) => {
    setSelected({
      kind: "formation",
      itemId: f.id,
      itemName: `${f.name} — ${f.title}`,
      price: f.price,
    });
    setOpen(true);
  };

  const handleCheckout = async (f) => {
    setLoadingId(f.id);
    try {
      const { checkout_url } = await createCheckout({
        lookup_key: LOOKUP_KEYS[f.id],
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (err) {
      setLoadingId(null);
      toast({
        title: "Paiement indisponible",
        description: "Impossible de démarrer le paiement pour le moment. Réessayez plus tard.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="formations" className="section-pad relative bg-[#08080a] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16"
        >
          <span className="eyebrow">Formations d'imports</span>
          <h2 className="section-title text-4xl md:text-6xl text-white mt-5">
            Devenez <span className="racing-text">autonome</span>
          </h2>
          <p className="mt-5 text-white/60 text-lg leading-relaxed">
            Vous préférez apprendre à importer vous-même ? Mes formations vous
            transmettent tout mon savoir-faire, étape par étape.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {FORMATIONS.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="card-lift group relative rounded-3xl overflow-hidden border border-white/8 bg-[#0e0e11]"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={f.image}
                  alt={f.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e11] via-[#0e0e11]/30 to-transparent" />
                <div className="absolute top-5 left-5 flex items-center gap-2 glass rounded-full px-4 py-2">
                  <GraduationCap size={16} className="text-[#ff6a00]" />
                  <span className="font-display font-semibold text-sm text-white tracking-wide">
                    {f.name}
                  </span>
                </div>
                <div className="absolute bottom-5 right-5 font-display font-extrabold text-3xl racing-text">
                  {f.price}
                </div>
              </div>

              <div className="p-8">
                <h3 className="font-display font-bold text-2xl text-white">{f.title}</h3>
                <p className="mt-3 text-white/55 leading-relaxed">{f.description}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {f.modules.map((m, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-white/75 text-sm">
                      <Check size={15} className="text-[#ff5a1f] flex-shrink-0 mt-0.5" />
                      {m}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => handleCheckout(f)}
                    disabled={loadingId === f.id}
                    className="btn-racing flex-1 justify-center disabled:opacity-70"
                  >
                    {loadingId === f.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <CreditCard size={16} />
                    )}
                    {loadingId === f.id ? "Redirection…" : `S'inscrire — ${f.price}`}
                  </button>
                  <button
                    onClick={() => handleQuestion(f)}
                    className="btn-ghost justify-center"
                  >
                    <MessageCircle size={16} /> Une question ?
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <BookingDialog open={open} onOpenChange={setOpen} item={selected} />
    </section>
  );
};

export default Formations;
